import Cookies from 'cookies-js'
import { isUndefined, mapValues, omitBy } from 'lodash-es'

import type { paths, operations } from '@23/openapi'

import { ENV_SETTINGS } from '../envSettings'
import { getSiteData } from '../blocs/useSite'
import { getLiveId } from '../blocs/roomInfo'

import { getWorkspaceToken } from './tokens'

const API_BASE_URL =
  ENV_SETTINGS.environment === 'production'
    ? 'https://api.twentythree.com'
    : 'https://api.dev.twentythree.com'

/// ========= util types ========== ///

type OmitUndefined<T> = {
  [K in keyof T as T[K] extends undefined ? never : K]: T[K]
}

type OptionalIfUndefined<T> = undefined extends T ? [param?: T] : [param: T]

type GetRelativePath<AbsolutePath extends string> =
  AbsolutePath extends `/api/{organizationId}/{workspaceId}/${infer Path}`
    ? Path
    : never

type GetRelativeWebinarPath<AbsolutePath extends string> =
  AbsolutePath extends `/api/{organizationId}/{workspaceId}/live/{liveId}/${infer Path}`
    ? Path
    : never

type GetRelativePublicWebinarPath<AbsolutePath extends string> =
  AbsolutePath extends `/api/public/{workspaceId}/live/{liveId}/${infer Path}`
    ? Path
    : never

type InjectParamType<Param extends string> = Param extends 'id' | `${string}Id`
  ? number
  : string

type InjectId<Path extends string> =
  Path extends `${infer Before}{${infer Param}}${infer After}`
    ? `${InjectId<Before>}${InjectParamType<Param>}${InjectId<After>}`
    : Path

type FullPathsMap = {
  [FullPath in keyof paths as GetRelativePath<FullPath>]: FullPath
} & {
  [FullPath in keyof paths as InjectId<GetRelativePath<FullPath>>]: FullPath
}

type FullWebinarPathsMap = {
  [FullPath in keyof paths as GetRelativeWebinarPath<FullPath>]: FullPath
} & {
  [FullPath in keyof paths as InjectId<
    GetRelativeWebinarPath<FullPath>
  >]: FullPath
}

type FullPublicWebinarPathsMap = {
  [FullPath in keyof paths as GetRelativePublicWebinarPath<FullPath>]: FullPath
} & {
  [FullPath in keyof paths as InjectId<
    GetRelativePublicWebinarPath<FullPath>
  >]: FullPath
}

/// ========= request types ========== ///

type AcceptedMethod = 'get' | 'put' | 'post' | 'delete' | 'patch'

type Methods<Path extends keyof paths> = keyof OmitUndefined<
  Pick<paths[Path], AcceptedMethod>
>

type Operation<
  Path extends keyof paths,
  Method extends Methods<Path>,
> = NonNullable<paths[Path][Method]>

type RequestData<
  Op extends operations[keyof operations],
  Method extends AcceptedMethod,
> = Method extends 'get'
  ? Op['parameters']['query']
  : Op['requestBody'] extends { content: infer Content }
    ? Content extends { 'application/json': infer JsonContent }
      ? JsonContent
      : undefined
    : undefined

/// ========= response types ========== ///

type AcceptCode = 200 | 201
type ErrorCode = 403 | 404 | 500
type ResponseCode = AcceptCode | ErrorCode

type Response<
  Op extends operations[keyof operations],
  Code extends ResponseCode,
> =
  Op['responses'][Code & keyof Op['responses']] extends Record<
    'content',
    Record<'application/json', unknown>
  >
    ? Op['responses'][Code &
        keyof Op['responses']]['content']['application/json']
    : undefined

/// ========== Requests handling ========== ///

const workspaceApiRequest = async <
  Path extends keyof FullPathsMap,
  Method extends Methods<FullPathsMap[Path]>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<
    // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
    RequestData<Operation<FullPathsMap[Path], Method>, Method>
  >
  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
): Promise<Response<Operation<FullPathsMap[Path], Method>, AcceptCode>> => {
  const siteData = await getSiteData()
  const token = await getWorkspaceToken()

  return performRequest(
    `/api/${siteData.subscription_id}/${siteData.site_id}/${path}`,
    token,
    // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
    method,
    data[0]
  )
}

const authenticatedWebinarApiRequest = async <
  Path extends keyof FullWebinarPathsMap,
  Method extends Methods<FullWebinarPathsMap[Path]>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<
    // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
    RequestData<Operation<FullWebinarPathsMap[Path], Method>, Method>
  >
): Promise<
  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
  Response<Operation<FullWebinarPathsMap[Path], Method>, AcceptCode>
> => {
  const liveId = getLiveId()
  const siteData = await getSiteData()
  const token = await getWorkspaceToken()

  const url = `/api/${siteData.subscription_id}/${siteData.site_id}/live/${liveId}/${path}`

  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
  return performRequest(url, token, method, data[0])
}

const publicWebinarApiRequest = async <
  Path extends keyof FullPublicWebinarPathsMap,
  Method extends Methods<FullPublicWebinarPathsMap[Path]>,
>(
  path: Path,
  method: Method,
  ...data: OptionalIfUndefined<
    // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
    RequestData<Operation<FullPublicWebinarPathsMap[Path], Method>, Method>
  >
): Promise<
  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
  Response<Operation<FullPublicWebinarPathsMap[Path], Method>, AcceptCode>
> => {
  const liveId = getLiveId()
  const siteData = await getSiteData()

  const url = `/api/public/${siteData.site_id}/live/${liveId}/${path}`

  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
  return performRequest(url, undefined, method, data[0])
}

const performRequest = async (
  path: string,
  bearerToken: string | undefined,
  method: AcceptedMethod,
  data?: Record<string, unknown>
) => {
  const fetchOptions = {
    method: method.toUpperCase(),
    headers: new Headers(),
  } as const

  if (bearerToken) {
    fetchOptions.headers.set('Authorization', `Bearer ${bearerToken}`)
  }
  // setting dev environment cookie as a header to circumvent cross-domain issues
  const cookie = Cookies.get('_tt_dev_env')
  if (cookie) {
    fetchOptions.headers.set('TwentyThree-Development-Environment', cookie)
  }

  // clean up unset data — explicit nulls used for unsetting values
  const cleanData = omitBy(data, isUndefined)

  const url = new URL(path, API_BASE_URL)

  let response
  if (['head', 'get'].includes(method)) {
    const stringifiedData = mapValues(cleanData, (value) => String(value))

    response = await fetch(
      `${url.href}?${new URLSearchParams(stringifiedData)}`,
      { ...fetchOptions }
    )
  } else {
    fetchOptions.headers.set('Content-Type', 'application/json')
    response = await fetch(url.href, {
      body: JSON.stringify(cleanData),
      ...fetchOptions,
    })
  }

  if (response.status >= 400 && response.status < 600) {
    const apiError = await response.json()
    throw new ApiError(path, method, response.status as ErrorCode, apiError)
  }

  try {
    return await response.json()
  } catch {
    throw new Error('Failed to parse response')
  }
}

/// ========= Errors ========== ///

class ApiError<Data, Code extends ErrorCode> extends Error {
  endpoint: string

  method: string

  response: Data

  code: Code

  constructor(endpoint: string, method: string, code: Code, response: Data) {
    super('API responded with an error.')
    this.endpoint = endpoint
    this.method = method
    this.code = code
    this.response = response
  }
}

const isApiError = <
  Path extends
    | keyof FullPathsMap
    | keyof FullWebinarPathsMap
    | keyof FullPublicWebinarPathsMap,
  FullPath extends Path extends keyof FullPathsMap
    ? FullPathsMap[Path]
    : Path extends keyof FullWebinarPathsMap
      ? FullWebinarPathsMap[Path]
      : Path extends keyof FullPublicWebinarPathsMap
        ? FullPublicWebinarPathsMap[Path]
        : never,
  Method extends Methods<FullPath>,
  Code extends ErrorCode,
>(
  error: unknown,
  _path?: Path,
  method?: Method,
  code?: Code
  // @ts-expect-error — typescript isn't inferring operations correctly; works down stream
): error is ApiError<Response<Operation<FullPath, Method>, Code>, Code> => {
  if (!(error instanceof ApiError)) return false
  if (method && error.method !== method) return false
  if (code && error.code !== code) return false
  // if(path && error.endpoint !== path) return false TODO:
  return true
}

type ValidationErrors = {
  errors: Record<string, Array<{ message: string }>>
}
// TODO: we should avoid the cast in the future
// maybe expose the response type through the openAPI types
const getValidationErrors = (error: unknown) => {
  try {
    if (
      error instanceof ApiError &&
      error.response.code === 'validationError'
    ) {
      return (error.response as ValidationErrors).errors
    }
  } catch (e) {
    console.error(e)
  }

  return null
}

export {
  workspaceApiRequest,
  authenticatedWebinarApiRequest,
  publicWebinarApiRequest,
  isApiError,
  getValidationErrors,
}
